import React, { memo, useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import uparrow from '../../../../assets/images/up.png';
import downarrow from '../../../../assets/images/down.png';

// ✅ WebCustom MenuList as a separate memoized component
const WebCustomMenuList = memo((props) => {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const [scrollInfo, setScrollInfo] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const scrollAmount = 100;

  // --- Update scroll info ---
  const updateScrollInfo = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollInfo({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollInfo();
    el.addEventListener("scroll", updateScrollInfo);
    window.addEventListener("resize", updateScrollInfo);
    return () => {
      el.removeEventListener("scroll", updateScrollInfo);
      window.removeEventListener("resize", updateScrollInfo);
    };
  }, []);

  // --- Long press scroll logic ---
  const startScrolling = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    // Do one immediate scroll
    el.scrollTo({
      top: el.scrollTop + (direction === "down" ? scrollAmount : -scrollAmount),
      behavior: "smooth",
    });

    // Start continuous scroll
    scrollIntervalRef.current = setInterval(() => {
      el.scrollTo({
        top: el.scrollTop + (direction === "down" ? scrollAmount / 3 : -scrollAmount / 3),
        behavior: "smooth",
      });
    }, 100);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // --- Thumb sizing ---
  const getThumbHeight = () => {
    const { clientHeight, scrollHeight } = scrollInfo;
    if (scrollHeight <= clientHeight) return 100;
    return Math.max((clientHeight / scrollHeight) * 100, 10);
  };

  const getThumbTop = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollInfo;
    if (scrollHeight <= clientHeight) return 0;
    return (scrollTop / (scrollHeight - clientHeight)) * (100 - getThumbHeight());
  };

  // --- Thumb drag start (mouse + touch) ---
  const handleThumbStart = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStartY(clientY);
    setStartScrollTop(scrollRef.current.scrollTop);
    e.preventDefault();
  };

  // --- Handle dragging ---
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const rect = track.getBoundingClientRect();
      const trackHeight = rect.height;
      const thumbHeight = (getThumbHeight() / 100) * trackHeight;
      const deltaY = clientY - dragStartY;
      const maxThumbTop = trackHeight - thumbHeight;
      const scrollRatio = (el.scrollHeight - el.clientHeight) / maxThumbTop;

      el.scrollTop = startScrollTop + deltaY * scrollRatio;
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, dragStartY, startScrollTop, scrollInfo]);

  // --- Click on scrollbar track ---
  const handleTrackClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // 👈 ADD THIS LINE
    const track = trackRef.current;
    const el = scrollRef.current;
    if (!track || !el) return;

    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const thumbHeight = (getThumbHeight() / 100) * rect.height;
    const thumbTop = (getThumbTop() / 100) * rect.height;

    if (clickY < thumbTop) {
      el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
    } else if (clickY > thumbTop + thumbHeight) {
      el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
    }
  };

  // --- Styles ---
  const arrowBtnStyle = {
    width: "32px",
    height: "32px",
    background: "#f9f9f9",
    border: "1px solid #bdbdbd",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    transition: "background 0.2s",
    userSelect: "none",
  };

  const arrowImgStyle = {
    width: "20px",
    height: "20px",
    objectFit: "contain",
    pointerEvents: "none",
  };

  // --- Check if scroll is needed ---
  const showScrollbar = scrollInfo.scrollHeight > scrollInfo.clientHeight;

  return (
    <div style={{ position: "relative" }}>
      <components.MenuList
        {...props}
        innerRef={scrollRef}
        style={{
          maxHeight: "260px",
          overflowY: "hidden",
          flex: 1,
          paddingRight: showScrollbar ? "40px" : "8px",
        }}
      >
        {props.children}
      </components.MenuList>

      {showScrollbar && (
        <div
          style={{
            position: "absolute",
            right: "6px",
            top: "8px",
            bottom: "8px",
            width: "32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Up Arrow */}
          {/* Up Arrow */}
          <div
            style={arrowBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startScrolling("up");
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startScrolling("up");
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
          >
            <img src={uparrow} alt="up" style={arrowImgStyle} />
          </div>


          {/* Track + Thumb */}
          <div
            ref={trackRef}
            onClick={handleTrackClick}
            style={{
              position: "relative",
              flexGrow: 1,
              width: "22px",
              background: "#eee",
              margin: "6px 0",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            <div
              onMouseDown={handleThumbStart}
              onTouchStart={handleThumbStart}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${getThumbTop()}%`,
                height: `${getThumbHeight()}%`,
                background: isDragging ? "#888" : "#a0a0a0",
                borderRadius: "6px",
                cursor: isDragging ? "grabbing" : "grab",
                transition: "background 0.2s, height 0.2s",
              }}
            />
          </div>

          {/* Down Arrow */}
          {/* Down Arrow */}
          <div
            style={arrowBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startScrolling("down");
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startScrolling("down");
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopScrolling();
            }}
          >
            <img src={downarrow} alt="down" style={arrowImgStyle} />
          </div>

        </div>
      )}
    </div>
  );
});

// ✅ MobileCustom MenuList as a separate memoized component
const MobileCustomMenuList = memo((props) => {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const [scrollInfo, setScrollInfo] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const scrollAmount = 100;

  // --- Update scroll info ---
  const updateScrollInfo = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollInfo({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollInfo();
    el.addEventListener("scroll", updateScrollInfo);
    window.addEventListener("resize", updateScrollInfo);
    return () => {
      el.removeEventListener("scroll", updateScrollInfo);
      window.removeEventListener("resize", updateScrollInfo);
    };
  }, []);

  // --- Scroll functions ---
  const scrollUp = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollTop - scrollAmount, behavior: "smooth" });
  };

  const scrollDown = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollTop + scrollAmount, behavior: "smooth" });
  };

  // --- Thumb sizing ---
  const getThumbHeight = () => {
    const { clientHeight, scrollHeight } = scrollInfo;
    if (scrollHeight <= clientHeight) return 100;
    return Math.max((clientHeight / scrollHeight) * 100, 10);
  };

  const getThumbTop = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollInfo;
    if (scrollHeight <= clientHeight) return 0;
    return (scrollTop / (scrollHeight - clientHeight)) * (100 - getThumbHeight());
  };

  // --- Thumb drag start (mouse + touch) ---
  const handleThumbStart = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDragging(true);
    setDragStartY(clientY);
    setStartScrollTop(scrollRef.current.scrollTop);
    e.preventDefault();
  };

  // --- Handle dragging ---
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const rect = track.getBoundingClientRect();
      const trackHeight = rect.height;
      const thumbHeight = (getThumbHeight() / 100) * trackHeight;
      const deltaY = clientY - dragStartY;
      const maxThumbTop = trackHeight - thumbHeight;
      const scrollRatio = (el.scrollHeight - el.clientHeight) / maxThumbTop;

      el.scrollTop = startScrollTop + deltaY * scrollRatio;
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, dragStartY, startScrollTop, scrollInfo]);

  // --- Click on scrollbar track ---
  const handleTrackClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    const el = scrollRef.current;
    if (!track || !el) return;

    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const thumbHeight = (getThumbHeight() / 100) * rect.height;
    const thumbTop = (getThumbTop() / 100) * rect.height;

    if (clickY < thumbTop) {
      el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
    } else if (clickY > thumbTop + thumbHeight) {
      el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
    }
  };

  const stopAllEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // --- Long press scroll logic ---
  const startScrolling = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    stopScrolling(); // clear any previous interval first

    // Immediate scroll
    el.scrollTo({
      top: el.scrollTop + (direction === "down" ? scrollAmount : -scrollAmount),
      behavior: "smooth",
    });

    // Start continuous scroll
    scrollIntervalRef.current = setInterval(() => {
      el.scrollTo({
        top: el.scrollTop + (direction === "down" ? scrollAmount / 3 : -scrollAmount / 3),
        behavior: "smooth",
      });
    }, 100);

    // ✅ Stop scrolling when user lifts finger or mouse anywhere
    const handleStop = (e) => {
      stopAllEvents(e);
      stopScrolling();
      window.removeEventListener("mouseup", handleStop, true);
      window.removeEventListener("touchend", handleStop, true);
      window.removeEventListener("touchcancel", handleStop, true);
    };

    window.addEventListener("mouseup", handleStop, true);
    window.addEventListener("touchend", handleStop, true);
    window.addEventListener("touchcancel", handleStop, true);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // --- Styles ---
  const arrowBtnStyle = {
    width: "32px",
    height: "32px",
    background: "#f9f9f9",
    border: "1px solid #bdbdbd",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    transition: "background 0.2s",
    userSelect: "none",
  };

  const arrowImgStyle = {
    width: "20px",
    height: "20px",
    objectFit: "contain",
    pointerEvents: "none",
  };

  // --- Check if scroll is needed ---
  const showScrollbar = scrollInfo.scrollHeight > scrollInfo.clientHeight;

  return (
    <div
      style={{ position: "relative" }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <components.MenuList
        {...props}
        innerRef={scrollRef}
        style={{
          maxHeight: "260px",
          overflowY: "hidden",
          flex: 1,
          paddingRight: showScrollbar ? "40px" : "8px",
        }}
      >
        {props.children}
      </components.MenuList>

      {showScrollbar && (
        <div
          style={{
            position: "absolute",
            right: "6px",
            top: "8px",
            bottom: "8px",
            width: "32px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Up Arrow */}
          <div
            ref={(el) => {
              if (!el) return;
              const handleStart = (e) => {
                e.preventDefault();
                e.stopPropagation();
                startScrolling("up");
              };
              const handleEnd = (e) => {
                e.preventDefault();
                e.stopPropagation();
                stopScrolling();
              };
              el.addEventListener("mousedown", handleStart, { capture: true });
              el.addEventListener("mouseup", handleEnd, { capture: true });
              el.addEventListener("touchstart", handleStart, { capture: true, passive: false });
              el.addEventListener("touchend", handleEnd, { capture: true, passive: false });
              el.addEventListener("touchcancel", handleEnd, { capture: true, passive: false });
            }}
            style={arrowBtnStyle}
          >
            <img src={uparrow} alt="up" style={arrowImgStyle} />
          </div>

          {/* Track + Thumb */}
          <div
            ref={trackRef}
            onClick={handleTrackClick}
            style={{
              position: "relative",
              flexGrow: 1,
              width: "22px",
              background: "#eee",
              margin: "6px 0",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            <div
              onMouseDown={handleThumbStart}
              onTouchStart={handleThumbStart}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${getThumbTop()}%`,
                height: `${getThumbHeight()}%`,
                background: isDragging ? "#888" : "#a0a0a0",
                borderRadius: "6px",
                cursor: isDragging ? "grabbing" : "grab",
                transition: "background 0.2s, height 0.2s",
              }}
            />
          </div>

          {/* Down Arrow */}
          <div
            ref={(el) => {
              if (!el) return;
              const handleStart = (e) => {
                e.preventDefault();
                e.stopPropagation();
                startScrolling("down");
              };
              const handleEnd = (e) => {
                e.preventDefault();
                e.stopPropagation();
                stopScrolling();
              };
              el.addEventListener("mousedown", handleStart, { capture: true });
              el.addEventListener("mouseup", handleEnd, { capture: true });
              el.addEventListener("touchstart", handleStart, { capture: true, passive: false });
              el.addEventListener("touchend", handleEnd, { capture: true, passive: false });
              el.addEventListener("touchcancel", handleEnd, { capture: true, passive: false });
            }}
            style={arrowBtnStyle}
          >
            <img src={downarrow} alt="down" style={arrowImgStyle} />
          </div>
        </div>
      )}
    </div>
  );
});

function SelectInput({
  options = [],
  value,
  name,
  handleChange,
  labelKey,
  valueKey,
  isMulti = false,
  defaultValue,
  isDisabled = false,
  closeMenuOnSelect = true,
  isLoading = false,
  isClearable = true,
  placeholder = "-- SELECT --",
  label,
  required,
  errorMessage = {},
  width = 100,
  height = "45px",
  isSearchable = false,
  isShowError = true,
  isshowCaps = false,
}) {
  // ---------- your existing logic ----------
  const selectedValue =
    options?.length > 0
      ? options?.find((option) => option[valueKey] === value)
      : null;

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height,
      minHeight: height,
      border:
        errorMessage && errorMessage[name]
          ? "1px solid rgb(200, 96, 94)"
          : "1px solid #ccc",
      "&:hover": {
        borderColor:
          errorMessage && errorMessage[name] ? "#c8605e" : base.borderColor,
      },
      boxShadow: state.isFocused
        ? errorMessage && errorMessage[name]
          ? "0 0 5px 1px #c8605e"
          : base.boxShadow
        : null,
      zIndex: state.isFocused ? 9999 : "auto",
      backgroundColor: isDisabled ? "#e9ecef" : "#ffffff",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      // Hide scrollbar but maintain functionality
      "&::-webkit-scrollbar": {
        width: "0px",
        height: "0px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "transparent",
      },
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE and Edge
    }),
    menuList: (base) => ({
      ...base,
      // Hide scrollbar for menu list
      "&::-webkit-scrollbar": {
        width: "0px",
        height: "0px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "transparent",
      },
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // IE and Edge
    }),

    option: (base, state) => ({
      ...base,
      color:
        state?.children?.includes("( New )") && !state?.isSelected
          ? "#c8605e"
          : base.color,
      backgroundColor: state.isSelected
        ? "#c8605e"
        : state.isFocused
          ? "#fae4e4"
          : base.backgroundColor,
      "&:hover": {
        backgroundColor: state.isSelected ? "#c8605e" : "#fae4e4",
      },
      width: "97%",
      fontSize: "15px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black",
    }),
  };

  const enhancedOptions = [
    { label: placeholder, value: "" },
    ...options.map((option) => ({
      label: isshowCaps ? option[labelKey]?.toUpperCase() : option[labelKey],
      value: option[valueKey],
    })),
  ];

  const platform = window.navigator.platform.toLowerCase();
  const [menuHeight, setMenuHeight] = useState(window.innerHeight - 320);

  useEffect(() => {
    const handleResize = () => setMenuHeight(window.innerHeight - 320);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={`w-${width} ${isDisabled ? "nocursor" : ""}`}>
      {label && (
        <label
          htmlFor={name}
          className="form-label col-form-label  w-100 font-size-15 form-label-custom-visible"
        >
          {label} {required && <span className="mandatory">*</span>}
        </label>
      )}

      <Select
        components={{
          MenuList: platform.includes("win") ? WebCustomMenuList : MobileCustomMenuList, // ✅ Stable separate component
        }}
        id={name}
        className="font-size-15"
        name={name}
        styles={customStyles}
        options={enhancedOptions}
        value={
          selectedValue
            ? {
              label: isshowCaps
                ? selectedValue[labelKey]?.toUpperCase()
                : selectedValue[labelKey],
              value: selectedValue[valueKey],
            }
            : null
        }
        isSearchable={platform.includes("win") ? isSearchable : false}
        onChange={handleChange}
        isMulti={isMulti}
        defaultValue={defaultValue}
        closeMenuOnSelect={closeMenuOnSelect}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={false}
        placeholder={placeholder}
        maxMenuHeight={menuHeight}
        menuPlacement="auto"
        inputMode="none"
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
      />

      {isShowError && (
        <div>
          {name === "stateNameID" && errorMessage ? (
            <div className="text-danger-new fw-700 fs-6 mt-1">
              {typeof errorMessage === "object" ? "" : errorMessage}
            </div>
          ) : (
            errorMessage?.[name] && (
              <div className="text-danger-new fw-700 fs-6 mt-1">
                {typeof errorMessage[name] === "object"
                  ? JSON.stringify(errorMessage[name])
                  : errorMessage[name]}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default memo(SelectInput);
